// Reports JavaScript errors in the browser to the platform log (POST /v1/client-logs), so they show up in admin.
// Only the page address without query string is sent (invite links carry a token there).

interface ErrorReportingOptions {
  app: "tenant" | "admin" | "player";
  apiBaseUrl?: string;
  getToken?: () => string | null;
}

const MAX_REPORTS_PER_PAGE = 10;
const SAME_ERROR_INTERVAL_MS = 60 * 1000;
// Expected responses (expired login, no access, not found) are handled by the app and are not errors here
const IGNORED = /Request failed with status code (401|403|404)|ResizeObserver loop/;

export const startErrorReporting = ({ app, apiBaseUrl, getToken }: ErrorReportingOptions) => {
  if (typeof window === "undefined" || !apiBaseUrl || (window as any).__breeferErrorReporting) return;
  (window as any).__breeferErrorReporting = true;

  let sent = 0;
  const lastSent = new Map<string, number>();

  const report = (message: string, stack?: string) => {
    if (!message || IGNORED.test(message) || sent >= MAX_REPORTS_PER_PAGE) return;
    const key = message.slice(0, 200);
    const now = Date.now();
    if ((lastSent.get(key) || 0) > now - SAME_ERROR_INTERVAL_MS) return;
    lastSent.set(key, now);
    sent += 1;

    let token: string | null = null;
    try {
      token = getToken ? getToken() : null;
    } catch {
      token = null;
    }
    fetch(`${apiBaseUrl}/v1/client-logs`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        app,
        level: "error",
        message: message.slice(0, 1000),
        stack: (stack || "").slice(0, 4000),
        url: `${window.location.origin}${window.location.pathname}`,
        userAgent: navigator.userAgent.slice(0, 500),
      }),
    }).catch(() => undefined);
  };

  window.addEventListener("error", (event) => report(event.message || String(event.error), event.error?.stack));
  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event.reason;
    report(reason?.message || String(reason), reason?.stack);
  });
};
